import { useEffect, useRef, useState } from 'react';
import { Exchange, Segment } from '@/types/symbols/exchangeTypes';
import { getPrice, normalizeWsExchange, prepareSSR } from '@/utils/symbols/exchangeUtils';

export const useExchangeWebSocket = (
    symbol: string,
    initialExchangeList: Exchange[] = [],
    segment: Segment,
) => {
    const isUSDT = (symbol || '').toLowerCase() === 'usdt';
    const [exchanges, setExchanges] = useState<Exchange[]>(() => prepareSSR(initialExchangeList));
    const [priceFlashes, setPriceFlashes] = useState<Record<string, 'up' | 'down' | undefined>>({});
    const [arbitrageProfit, setArbitrageProfit] = useState<string | null>(null);
    const [profitDirection, setProfitDirection] = useState<'up' | 'down' | 'none'>('none');
    const prevProfitRef = useRef<number | null>(null);

    const allowedSlugsRef = useRef<Set<string>>(new Set());
    const ssrOrderRef = useRef<string[]>([]);

    useEffect(() => {
        const normalizedSSR = prepareSSR(initialExchangeList);
        setExchanges(normalizedSSR);

        const slugs = normalizedSSR.map((e) => e.slug);
        allowedSlugsRef.current = new Set(slugs);
        ssrOrderRef.current = slugs.slice();
    }, [initialExchangeList]);

    useEffect(() => {
        if (!isUSDT) return;

        const ws = new WebSocket('wss://market-pulse-ws.khanes.app/ws/exchanges/symbol/usdt');

        ws.onmessage = (evt) => {
            try {
                const msg = JSON.parse(evt.data);
                if (msg?.type !== 'exchange_update' || !msg?.data) return;

                if (msg.data.profit != null) {
                    const newProfit = Number(msg.data.profit);
                    const oldProfit = prevProfitRef.current;

                    if (oldProfit !== null) {
                        if (newProfit > oldProfit) setProfitDirection('up');
                        else if (newProfit < oldProfit) setProfitDirection('down');
                    }

                    setArbitrageProfit(String(newProfit));
                    prevProfitRef.current = newProfit; // Update ref for next message
                }

                if (Array.isArray(msg.data.exchanges)) {
                    const normalized = (msg.data.exchanges as any[])
                        .map(normalizeWsExchange)
                        .filter(Boolean) as Exchange[];

                    const allowed = allowedSlugsRef.current;
                    const incomingMap = new Map<string, Exchange>();
                    for (const e of normalized) {
                        if (allowed.has(e.slug)) {
                            incomingMap.set(e.slug, e);
                        }
                    }

                    setExchanges((prev) => {
                        const prevMap = new Map(prev.map((p) => [p.slug, p]));
                        const flashes: Record<string, 'up' | 'down' | undefined> = {};

                        const merged = ssrOrderRef.current.map((slug) => {
                            const oldRow = prevMap.get(slug);
                            const newRow = incomingMap.get(slug);
                            if (oldRow && newRow) {
                                const oldPrice = getPrice(oldRow, segment);
                                const newPrice = getPrice(newRow, segment);
                                if (newPrice > oldPrice) flashes[slug] = 'up';
                                else if (newPrice < oldPrice) flashes[slug] = 'down';
                                return {
                                    ...newRow,
                                    rating: newRow.rating || oldRow.rating || 0,
                                };
                            }
                            return oldRow;
                        });

                        setPriceFlashes(flashes);
                        setTimeout(() => setPriceFlashes({}), 1200);

                        const seen = new Set<string>();
                        const clean: Exchange[] = [];
                        for (const row of merged) {
                            if (!row) continue;
                            if (seen.has(row.slug)) continue;
                            seen.add(row.slug);
                            clean.push(row);
                        }
                        return clean;
                    });
                }
            } catch (e) {
                console.error('[EX WS] parse error', e);
            }
        };

        let disposed = false;
        // A browser error Event has no useful fields; only log real failures, not our own teardown
        ws.onerror = () => {
            if (!disposed) console.warn('[EX WS] connection error');
        };
        return () => {
            disposed = true;
            ws.onmessage = null;
            // Closing while CONNECTING makes the browser report an error, so wait for the open
            if (ws.readyState === WebSocket.CONNECTING) {
                ws.onopen = () => ws.close();
                return;
            }
            try {
                ws.close();
            } catch {}
        };
    }, [isUSDT, segment]);

    // Clear flashes when switching tabs
    useEffect(() => {
        setPriceFlashes({});
    }, [segment]);

    return { exchanges, priceFlashes, arbitrageProfit, profitDirection, isUSDT };
};
